import { useMemo, useSyncExternalStore } from 'react';
import type { PuzzleType } from '@il-colpo/engine';
import { create } from 'zustand';
import type { PracticeClient } from '../local/practiceSession.ts';
import { DEBUG_ENABLED } from './gameStore.ts';

export interface TrainingSetup {
  type: PuzzleType;
  level: number;
  humanName: string;
  language: 'it' | 'en';
  /** Fixed seed (tests, replays); random when absent. */
  seed?: string;
}

interface TrainingStore {
  client: PracticeClient | null;
  setup: TrainingSetup | null;
  /** True while a training is being started, so that the screen does not mistake it for none. */
  starting: boolean;
  start: (setup: TrainingSetup) => Promise<void>;
  leave: () => void;
}

function randomSeed(): string {
  const bytes = new Uint32Array(2);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(36)).join('');
}

export const useTrainingStore = create<TrainingStore>((set, get) => ({
  client: null,
  setup: null,
  starting: false,
  async start(setup) {
    get().client?.dispose();
    set({ setup, client: null, starting: true });
    const { createPracticeSession } = await import('../local/practiceSession.ts');
    const params = DEBUG_ENABLED ? new URLSearchParams(window.location.search) : null;
    const speed = Number(params?.get('speed') ?? 1);
    const client = createPracticeSession({
      ...setup,
      seed: setup.seed ?? params?.get('seed') ?? randomSeed(),
      ...(speed > 1 ? { speed } : {}),
    });
    if (DEBUG_ENABLED) (window as unknown as { __colpo?: unknown }).__colpo = { client };
    set({ client, starting: false });
  },
  leave() {
    get().client?.dispose();
    set({ client: null, starting: false });
  },
}));

/** The training client, re-rendered on every change. Null while there is none. */
export function useTrainingClient(): PracticeClient | null {
  const client = useTrainingStore((s) => s.client);
  const subscribe = useMemo(
    () => (listener: () => void) => client?.subscribe(listener) ?? (() => undefined),
    [client],
  );
  useSyncExternalStore(subscribe, () => client?.version() ?? 0);
  return client;
}
